// src/components/store/sections/deal/Variant2PastelCarousel.tsx
"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Star,
  ShoppingBag,
  Clock,
  Flame,
  ArrowRight,
  Heart,
  Package,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useProductTimer } from "./useProductTimer";

interface Props {
  products: any[];
  subdomain: string;
}

// 🌟 স্ক্রিনশটের ৪টি ইউনিক ব্যাকগ্রাউন্ড স্টেজ প্যালেট
const CARD_THEMES = [
  {
    // কার্ড ১: স্প্রিং গ্রিন গার্ডেন আর্চ
    bgGradient: "from-[#dcfce7] via-[#f0fdf4] to-[#bbf7d0]",
    pedestalGlow: "bg-emerald-500/25",
    categoryColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    buttonColor: "bg-[#6366f1] hover:bg-[#4f46e5]",
    stockColor: "text-rose-600",
  },
  {
    // কার্ড ২: ডার্ক প্লাম পার্পল স্প্ল্যাশ আর্চ
    bgGradient: "from-[#240b36] via-[#1a0826] to-[#0c0213]",
    pedestalGlow: "bg-purple-600/35",
    categoryColor: "bg-purple-50 text-purple-700 border-purple-200",
    buttonColor: "bg-[#6366f1] hover:bg-[#4f46e5]",
    stockColor: "text-emerald-600",
  },
  {
    // কার্ড ৩: সানলিট বোটানিক্যাল উড আর্চ
    bgGradient: "from-[#ecfccb] via-[#f7fee7] to-[#d9f99d]",
    pedestalGlow: "bg-lime-600/25",
    categoryColor: "bg-lime-50 text-lime-800 border-lime-200",
    buttonColor: "bg-[#059669] hover:bg-[#047857]",
    stockColor: "text-emerald-600",
  },
  {
    // কার্ড ৪: সাইবার নিয়ন ডার্ক ওয়েভ আর্চ
    bgGradient: "from-[#0a122e] via-[#05091a] to-[#020512]",
    pedestalGlow: "bg-cyan-500/40",
    categoryColor: "bg-blue-50 text-blue-700 border-blue-200",
    buttonColor: "bg-[#4338ca] hover:bg-[#3730a3]",
    stockColor: "text-blue-600",
  },
];

export default function Variant2PastelCarousel({ products, subdomain }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const resolveImageUrl = (raw?: string | null) => {
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) {
      if (raw.includes("127.0.0.1:8000")) {
        return raw.replace("127.0.0.1:8000", `${subdomain}.localhost:8000`);
      }
      return raw;
    }
    const clean = raw.startsWith("/") ? raw : `/${raw}`;
    return `http://${subdomain}.localhost:8000${clean}`;
  };

  const handleScroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      const scrollDistance = 510;
      scrollRef.current.scrollBy({
        left: dir === "left" ? -scrollDistance : scrollDistance,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 font-sans select-none space-y-6">
      {/* 🌟 হেডার সেকশন */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-black shadow-xs">
            <Flame className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
            <span>Hot Deals</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600">
              Deals
            </span>{" "}
            of the Day ({products.length})
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 font-medium">
            Premium quality products at unbeatable prices. Don&apos;t miss out!
          </p>
        </div>

        {/* অ্যারো কন্ট্রোল এবং View All Deals বাটন */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => handleScroll("left")}
            type="button"
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center transition-all shadow-xs active:scale-95 cursor-pointer"
            title="Scroll Left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleScroll("right")}
            type="button"
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center transition-all shadow-xs active:scale-95 cursor-pointer"
            title="Scroll Right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <Link
            href="/products"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-fuchsia-600 hover:opacity-95 text-white font-bold text-xs shadow-md transition-all active:scale-95"
          >
            <span>View All Deals</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* 🌟 ক্যারোসেল কন্টেইনার (হরিজন্টাল কার্ডস) */}
      <div
        ref={scrollRef}
        className="flex items-stretch gap-6 overflow-x-auto pb-4 pt-1 snap-x snap-mandatory scroll-smooth scrollbar-none"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {products.map((p: any, idx: number) => (
          <div
            key={p.id || idx}
            className="w-[340px] sm:w-[460px] lg:w-[490px] flex-shrink-0 snap-start"
          >
            <PastelArchCard
              product={p}
              theme={CARD_THEMES[idx % CARD_THEMES.length]}
              resolveImageUrl={resolveImageUrl}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

// 🌟 একক কার্ড কম্পোনেন্ট
function PastelArchCard({
  product,
  theme,
  resolveImageUrl,
}: {
  product: any;
  theme: (typeof CARD_THEMES)[0];
  resolveImageUrl: (url?: string) => string | null;
}) {
  const timer = useProductTimer(product);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const currentPrice = Number(product?.price || 0);
  const oldPrice = product?.compare_price ? Number(product.compare_price) : null;
  const discountPercent =
    oldPrice && oldPrice > currentPrice
      ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
      : 33;

  const stock = Number(product?.stock_quantity ?? product?.stock ?? 0);
  const imgUrl = resolveImageUrl(product?.primary_image || product?.image);
  const slug = product?.slug || product?.id || "";

  return (
    <div className="bg-white rounded-[32px] p-4 sm:p-5 shadow-[0_10px_35px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col md:flex-row gap-4 sm:gap-5 h-full relative group hover:shadow-[0_16px_45px_rgba(0,0,0,0.09)] transition-all duration-300">
      {/* ১. বাম পাশের ৩D আর্চ ব্যাকগ্রাউন্ড প্লেট */}
      <div
        className={`relative w-full md:w-[210px] lg:w-[225px] h-[220px] md:h-auto rounded-[26px] bg-gradient-to-b ${theme.bgGradient} overflow-hidden flex-shrink-0 flex items-center justify-center p-3 select-none`}
      >
        {/* ডিসকাউন্ট ব্যাজ */}
        <div className="absolute top-3 left-3 z-20">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500 text-white font-black text-[10px] shadow-sm tracking-wide">
            <Flame className="w-3 h-3 fill-white" />
            <span>{discountPercent}% OFF</span>
          </span>
        </div>

        {/* ৩D পেডেস্টাল আভা */}
        <div
          className={`absolute bottom-3 w-36 h-8 rounded-full ${theme.pedestalGlow} blur-md`}
        />

        {/* প্রোডাক্ট ইমেজ */}
        <div className="relative w-full h-full flex items-center justify-center z-10">
          {imgUrl ? (
            <Image
              src={imgUrl}
              alt={product?.name || "Deal Item"}
              fill
              unoptimized
              className="object-contain p-2 group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_12px_20px_rgba(0,0,0,0.3)]"
            />
          ) : (
            <div className="text-slate-400 text-xs font-bold">No Image</div>
          )}
        </div>
      </div>

      {/* ২. ডান পাশের ডিটেইলস, টাইমার ও বাটন */}
      <div className="flex-1 flex flex-col justify-between space-y-3">
        <div>
          {/* ক্যাটাগরি ও হার্ট বাটন */}
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span
              className={`px-3 py-0.5 rounded-full text-[10px] font-bold border truncate max-w-[130px] ${theme.categoryColor}`}
            >
              {product?.category_name || "Special Deal"}
            </span>

            <button
              type="button"
              onClick={() => setIsWishlisted(!isWishlisted)}
              className="w-8 h-8 rounded-full border border-slate-100 flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors shadow-2xs cursor-pointer active:scale-90"
              title="Add to Wishlist"
            >
              <Heart
                className={`w-4 h-4 transition-colors ${
                  isWishlisted ? "fill-rose-500 text-rose-500" : ""
                }`}
              />
            </button>
          </div>

          {/* প্রোডাক্ট টাইটেল */}
          <Link
            href={`/products/${slug}`}
            className="text-base sm:text-lg font-black text-slate-900 hover:text-purple-600 transition-colors line-clamp-1 block leading-snug"
          >
            {product?.name || "Deal Item"}
          </Link>

          {/* স্টার রেটিং */}
          <div className="flex items-center gap-1 text-xs font-bold text-amber-500 mt-1">
            <Star className="w-3.5 h-3.5 fill-amber-400" />
            <span>5.0 (124 reviews)</span>
          </div>

          {/* পার্পল প্রাইস */}
          <div className="flex items-baseline gap-2 mt-2 font-mono">
            <span className="text-2xl font-black text-purple-600 tracking-tight">
              ৳{currentPrice.toLocaleString()}
            </span>
            {oldPrice && (
              <span className="text-xs text-slate-400 line-through">
                ৳{oldPrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* ৩. লাইভ কাউন্টডাউন টাইমার (Hours, Minutes, Seconds লেবেলসহ) */}
        <div className="bg-slate-50/90 rounded-2xl p-2.5 border border-slate-100/90 space-y-1.5">
          <div className="flex items-center gap-1 text-[11px] font-bold text-rose-500">
            <Clock className="w-3.5 h-3.5" />
            <span>Hurry Up! Offer ends in:</span>
          </div>

          <div className="flex items-center justify-between text-center font-mono">
            <div className="flex-1 bg-white py-1 px-1.5 rounded-xl border border-slate-200/80 shadow-2xs">
              <span className="text-xs sm:text-sm font-black text-slate-800 block leading-tight">
                {String(timer.hours).padStart(2, "0")}h
              </span>
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block">
                Hours
              </span>
            </div>

            <span className="px-1 text-slate-400 font-bold">:</span>

            <div className="flex-1 bg-white py-1 px-1.5 rounded-xl border border-slate-200/80 shadow-2xs">
              <span className="text-xs sm:text-sm font-black text-slate-800 block leading-tight">
                {String(timer.minutes).padStart(2, "0")}m
              </span>
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block">
                Minutes
              </span>
            </div>

            <span className="px-1 text-slate-400 font-bold">:</span>

            <div className="flex-1 bg-white py-1 px-1.5 rounded-xl border border-slate-200/80 shadow-2xs">
              <span className="text-xs sm:text-sm font-black text-rose-600 block leading-tight">
                {String(timer.seconds).padStart(2, "0")}s
              </span>
              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider block">
                Seconds
              </span>
            </div>
          </div>
        </div>

        {/* ৪. ইনভেন্টরি স্টক ও Shop Deal বাটন */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
            <Package className="w-4 h-4 text-purple-600" />
            <span>Available:</span>
            <strong className={stock > 0 ? theme.stockColor : "text-rose-600"}>
              {stock} items
            </strong>
          </div>

          <Link
            href={`/products/${slug}`}
            className={`px-5 py-2.5 rounded-full ${theme.buttonColor} text-white font-black text-xs shadow-md flex items-center gap-1.5 active:scale-95 transition-all cursor-pointer`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Shop Deal</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}